/* eslint-disable @typescript-eslint/no-empty-object-type */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unsafe-function-type */
// Third-party library imports
import { get, isEqual, uniqWith } from 'lodash-es';
import {
    createSlice,
    PayloadAction,
    createAction,
    createSelector
} from '@reduxjs/toolkit';

// Local imports - Constants
import {
    CREDITS_PAGE,
    DETAILS_TAB
} from '../../config/tableAndPageConstants';
import { conversationTableFilterOptions } from '../../constants/common';
import {
    creditsSortByOptions,
    creditsStateFilterOptions,
    creditChangesTableFilterOptions,
    rebatesStateFilterOptions,
    rebatesSortByOptions,
} from '../../constants/creditsSortAndFilters';

// Local imports - Project-specific modules & types
import {
    CreditsState,
    Credit,
    GetCreditsRequestPayload,
    GetCreditChangesRequestPayload,
    CreditChanges,
    CreditAddCommentRequestPayload,
    DeleteCreditManuallyRequestPayload,
    RequestCreateCreditManuallyPayload,
    RequestEditCreditManuallyPayload,
    CreditsFetchedForOrganisationPayload,
    GetRebatesRequestPayload
} from './types';
import {
    PageData,
    Conversation,
    GetConversationRequestPayload
} from '../common/types';
import { GetInvoicesRequestPayload, Invoice } from '../invoices/types';

// Local imports - Utility functions & interfaces
import { DynamicObject } from '../../utils/commonInterfaces';

// Local imports - Redux store
import type { RootState } from '../storeTypes';

// Type-safe initialState!
export const initialState: CreditsState = {
    loading: false,
    saveCreditDataLoading: false,
    errorMessages: [],
    data: [],
    pageData: {
        pageSize: CREDITS_PAGE.pageSize,
        currentPage: 0,
        hasNextPage: false,
    },
    filters: {},
    tableFilter: undefined,
    creditState: get(creditsStateFilterOptions, '0.value'),
    sortBy: creditsSortByOptions[0].value, //0 for Credit number
    sortAscending: true,
    activeData: {
        record: {},
        conversation: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            conversationTableFilter: get(
                conversationTableFilterOptions,
                '0.value'
            ),
            sortBy: 'Created Date Time',
            sortAscending: false,
        },
        creditChanges: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CREDIT_CHANGES_TIMELINE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            creditChangesTableFilter: get(
                creditChangesTableFilterOptions,
                '0.value'
            ),
            sortBy: 'Created Date Time',
            sortAscending: false,
        },
        rebates: {
            loading: false,
            saveCreditDataLoading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: CREDITS_PAGE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            tableFilter: undefined,
            creditState: get(rebatesStateFilterOptions, '0.value'),
            sortBy: rebatesSortByOptions[0].value, //0 for Credit number
            sortAscending: true,
        },
        loading: false,
        selectedId: null,
        errorMessages: [],
    },
};

// Thanks to Redux 4's much simpler typings, we can take away a lot of typings on the reducer side,
// everything will remain type-safe.
const creditSlice = createSlice({
    name: 'credit',
    initialState,
    reducers: {
        getCreditsRequestAction: (
            state,
            _action: PayloadAction<GetCreditsRequestPayload>
        ) => {
            state.loading = true;
        },
        getCreditsForOrganisationRequestAction: (
            state,
            _action: PayloadAction<GetCreditsRequestPayload>
        ) => {
            state.loading = true;
        },
        getCreditsSuccessAction: (
            state,
            action: PayloadAction<{
                data: Credit[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.data = action.payload.data;
            }
            else {
                state.data = uniqWith(
                    [...state.data, ...action.payload.data],
                    isEqual
                );
            }

            state.loading = false;
            state.pageData = action.payload.pageData;
            state.errorMessages = [];
        },
        getCreditsErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.loading = false;
            state.data = [];
            state.errorMessages = action.payload;
        },
        updateCreditsFiltersAction: (
            state,
            action: PayloadAction<GetCreditsRequestPayload['filters']>
        ) => {
            state.filters = action.payload;
        },
        updateCreditsSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetCreditsRequestPayload['sortBy'];
                sortAscending: boolean;
                creditState: string;
            }>
        ) => {
            state.sortBy = action.payload.sortBy;
            state.sortAscending = action.payload.sortAscending;
            state.creditState = action.payload.creditState
        },
        updateCreditsTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.tableFilter = action.payload;
        },
        clearCreditsStateAllTableFiltersAction: (state) => {
            state.filters = initialState.filters;
            state.sortBy = initialState.sortBy;
            state.sortAscending = initialState.sortAscending;
            state.creditState = initialState.creditState;
            state.tableFilter = initialState.tableFilter;
        },
        clearCreditsStateDataAction: (state) => {
            Object.assign(state, initialState);
        },
        clearCreditsListData: (state) => {
            state.loading = false;
            state.data = [];
            state.pageData = initialState.pageData;
            state.errorMessages = [];
        },
        setCreditSelectedIdRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    creditId: string | null;
                    callback: () => void;
                }>
            ) => {
                state.activeData.selectedId = initialState.activeData.selectedId;
            },
            prepare: (
                creditId: string | null,
                callback: () => void
            ) => ({
                payload: { creditId, callback }
            })
        },
        setCreditSelectedIdSuccessAction: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.activeData.selectedId = action.payload;
        },
        getOrganisationCreditDataRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    creditId: string,
                    region: string,
                    isUsingCloudImportType: boolean,
                    companyId: string,
                    conversionCurrency: number,
                    currencies: DynamicObject[]
                }>
            ) => {
                state.activeData.record = initialState.activeData.record;
                state.activeData.loading = true;
            },
            prepare: (
                creditId: string,
                region: string,
                isUsingCloudImportType: boolean,
                companyId: string,
                conversionCurrency: number,
                currencies: DynamicObject[]
            ) => {
                return {
                    payload: {
                        creditId,
                        region,
                        isUsingCloudImportType,
                        companyId,
                        conversionCurrency,
                        currencies
                    }
                }
            }
        },
        getCreditDataRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    creditId: string,
                    isUsingCloudImportType: boolean
                }>
            ) => {
                state.activeData.record = initialState.activeData.record;
                state.activeData.loading = true;
            },
            prepare: (
                creditId: string,
                isUsingCloudImportType: boolean
            ) => {
                return {
                    payload: {
                        creditId,
                        isUsingCloudImportType
                    }
                }
            }
        },
        getCreditDataSuccessAction: (
            state,
            action: PayloadAction<{ record: {} }>
        ) => {
            const { record } = action.payload;
            state.activeData.record = record;
            state.activeData.loading = false;
            state.activeData.errorMessages = [];
        },
        getCreditDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.record = { ...initialState.activeData.record };
            state.activeData.loading = false;
            state.activeData.errorMessages = action.payload;
        },
        clearCreditDataAction: (state) => {
            state.activeData = initialState.activeData;
        },
        getCreditConversationRequestAction:  (
            state,
            action: PayloadAction<GetConversationRequestPayload>
        ) => {
            state.activeData.conversation.loading = true;
        },
        getCreditConversationSuccessAction: (
            state,
            action: PayloadAction<{
                data: Conversation[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.conversation.data = action.payload.data;
            }
            else {
                const existingData = state.activeData.conversation.data || [];
                state.activeData.conversation.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.activeData.conversation.loading = false;
            state.activeData.conversation.pageData = action.payload.pageData;
            state.activeData.conversation.errorMessages = [];
        },
        getCreditConversationErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.conversation.loading = false;
            state.activeData.conversation.data = [];
            state.activeData.conversation.errorMessages = action.payload;
        },
        updateCreditConversationFiltersAction: (
            state,
            action: PayloadAction<GetConversationRequestPayload['filters']>
        ) => {
            Object.assign(state.activeData.conversation.filters, action.payload);
        },
        updateCreditConversationTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.conversation.conversationTableFilter = action.payload;
        },
        updateCreditConversationSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetConversationRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.activeData.conversation.sortBy = action.payload.sortBy;
            state.activeData.conversation.sortAscending = action.payload.sortAscending;
        },
        getCreditChangesRequestAction: (
            state,
            _action: PayloadAction<GetCreditChangesRequestPayload>
        ) => {
            state.activeData.creditChanges.loading = true;
        },
        getOrganisationCreditChangesRequestAction: {
            reducer: (
                state,
                action: PayloadAction<{
                    payload: GetCreditChangesRequestPayload,
                    region: string,
                    companyIds: string[]
                }>
            ) => {
                state.activeData.creditChanges.loading = true;
            },
            prepare: (
                payload: GetCreditChangesRequestPayload,
                region: string,
                companyIds: string[]
            ) => {
                return {
                    payload: {
                        payload,
                        region,
                        companyIds
                    }
                }
            }
        },
        getCreditChangesSuccessAction: (
            state,
            action: PayloadAction<{
                data: CreditChanges[];
                pageData: PageData;
            }>
        ) => {
            const { data } = action.payload;
            const creditData = data as unknown as Credit[];
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.creditChanges.data = creditData;
            }
            else {
                const existingData = state.activeData.creditChanges.data || [];
                state.activeData.creditChanges.data = uniqWith(
                    [
                        ...existingData, 
                        ...creditData
                    ], 
                    isEqual
                );
            }

            state.activeData.creditChanges.loading = false;
            state.activeData.creditChanges.pageData = action.payload.pageData;
            state.activeData.creditChanges.errorMessages = [];
        },
        getCreditChangesErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.creditChanges.loading = false;
            state.activeData.creditChanges.data = [];
            state.activeData.creditChanges.errorMessages = action.payload;
        },
        updateCreditChangesFiltersAction: (
            state,
            action: PayloadAction<GetCreditChangesRequestPayload['filters']>
        ) => {
            Object.assign(state.activeData.creditChanges.filters, action.payload);
        },
        updateCreditChangesTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.creditChanges.creditChangesTableFilter = action.payload;
        },
        updateCreditChangesSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetCreditChangesRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.activeData.creditChanges.sortBy = action.payload.sortBy;
            state.activeData.creditChanges.sortAscending = action.payload.sortAscending;
        },
        getRebatesRequestAction: (
            state,
            _action: PayloadAction<GetRebatesRequestPayload>
        ) => {
            state.loading = true;
        },
        getRebatesErrorAction: (state, action: PayloadAction<string[]>) => {
            const { payload } = action;
            state.activeData.rebates.loading = false;
            state.activeData.rebates.data = initialState.activeData.rebates.data;
            state.activeData.rebates.errorMessages = payload;
        },
        getRebatesSuccessAction: (
            state, 
            action: PayloadAction<{
                data: Credit[],
                pageData: PageData
            }>
        ) => {
            const { data, pageData } = action.payload;

            if (pageData.currentPage === 0) {
                state.activeData.rebates.data = data;
            } else {
                state.activeData.rebates.data = uniqWith(
                    [...state.activeData.rebates.data, ...data],
                    isEqual
                )
            }
            
            state.activeData.rebates.loading = false;
            state.activeData.rebates.pageData = pageData;
            state.activeData.rebates.errorMessages = initialState.activeData.rebates.errorMessages;
        }
    }
});

const { actions, reducer } = creditSlice;

// Actions
export const {
    getCreditsRequestAction,
    getCreditsForOrganisationRequestAction,
    getCreditsSuccessAction,
    getCreditsErrorAction,
    updateCreditsFiltersAction,
    updateCreditsSortByAndStateAction,
    updateCreditsTableFilterAction,
    clearCreditsStateAllTableFiltersAction,
    clearCreditsStateDataAction,
    clearCreditsListData,
    setCreditSelectedIdRequestAction,
    setCreditSelectedIdSuccessAction,
    getOrganisationCreditDataRequestAction,
    getCreditDataRequestAction,
    getCreditDataSuccessAction,
    getCreditDataErrorAction,
    clearCreditDataAction,
    getCreditConversationRequestAction,
    getCreditConversationSuccessAction,
    getCreditConversationErrorAction,
    updateCreditConversationFiltersAction,
    updateCreditConversationTableFilterAction,
    updateCreditConversationSortByAction,
    getCreditChangesRequestAction,
    getOrganisationCreditChangesRequestAction,
    getCreditChangesSuccessAction,
    getCreditChangesErrorAction,
    updateCreditChangesFiltersAction,
    updateCreditChangesTableFilterAction,
    updateCreditChangesSortByAction,
    getRebatesRequestAction,
    getRebatesErrorAction,
    getRebatesSuccessAction
} = actions;

export const getCreditsByCreditCodesRequestAction = createAction(
    'credit/getCreditsByCreditCodesRequestAction',
    function prepare(
        payload: any,
        callback?: Function
    ) {
        return {
            payload: { payload, callback }
        };
    }
);

export const getCreditsByCreditCodesSuccessAction = createAction(
    'credit/getCreditsByCreditCodesSuccessAction',
    function prepare(data: Credit[]) {
        return {
            payload: { data }
        };
    }
);

export const getCreditsByCreditCodesErrorAction = createAction(
    'credit/getCreditsByCreditCodesErrorAction',
    function prepare(payload: string[]) {
        return {
            payload
        };
    }
);

export const getCreditInvoicesRequestAction = createAction(
    'credit/getCreditInvoicesRequestAction',
    function prepare(payload: GetInvoicesRequestPayload) {
        return {
            payload
        };
    }
);

export const getCreditInvoicesSuccessAction = createAction(
    'credit/getCreditInvoicesSuccessAction',
    function prepare(payload: {
        data: Invoice[],
        pageData: PageData
    }) {
        return {
            payload
        };
    }
);

export const getCreditInvoicesErrorAction = createAction(
    'credit/getCreditInvoicesErrorAction',
    function prepare(payload: string[]) {
        return {
            payload
        };
    }
);

export const updateCreditInvoicesFiltersAction = createAction(
    'credit/updateCreditInvoicesFiltersAction',
    function prepare(filters: GetInvoicesRequestPayload['filters']) {
        return {
            payload: { filters }
        };
    }
);

export const updateCreditInvoicesSortByAndStateAction = createAction(
    'credit/updateCreditInvoicesSortByAndStateAction',
    function prepare(
        sortBy: GetInvoicesRequestPayload['sortBy'],
        sortAscending: boolean,
        invoiceState: string
    ) {
        return {
            payload: { sortBy, sortAscending, invoiceState }
        };
    }
);

export const updateCreditInvoicesTableFilterAction = createAction(
    'credit/updateCreditInvoicesTableFilterAction',
    function prepare(invoiceState: string) {
        return {
            payload: { invoiceState }
        };
    }
);

export const creditAddCommentRequestAction = createAction(
    'credit/creditAddCommentRequestAction',
    function prepare(payload: CreditAddCommentRequestPayload) {
        return {
            payload
        };
    }
);

export const deleteCreditManuallyRequestAction = createAction(
    'credit/deleteCreditManuallyRequestAction',
    function prepare(payload: DeleteCreditManuallyRequestPayload) {
        return {
            payload
        };
    }
);

export const getCreditCustomFieldsForCompanyRequestAction = createAction(
    'credit/getCreditCustomFieldsForCompanyRequestAction',
    function prepare(
        companyId: string,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { companyId, callback }
        };
    }
);

export const getCreditCustomFieldsForOrganisationRequestAction = createAction(
    'credit/getCreditCustomFieldsForOrganisationRequestAction',
    function prepare(
        CompanyIds: string[],
        FieldName?: string[],
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { CompanyIds, FieldName, callback }
        };
    }
);

export const createCreditManuallyRequestAction = createAction(
    'credit/createCreditManuallyRequestAction',
    function prepare(
        payload: RequestCreateCreditManuallyPayload
    ) {
        return {
            payload
        };
    }
);

export const editCreditManuallyRequestAction = createAction(
    'credit/editCreditManuallyRequestAction',
    function prepare(
        payload: RequestEditCreditManuallyPayload
    ) {
        return {
            payload
        };
    }
);

export const getCreditsReconciliationReportRequestAction = createAction(
    'credit/getCreditsReconciliationReportRequestAction',
    function prepare(payload: DynamicObject) {
        return {
            payload
        };
    }
);

export const creditsFetchedForOrganisationAction = createAction(
    'credit/creditsFetchedForOrganisationAction',
    function prepare(payload: CreditsFetchedForOrganisationPayload) {
        return {
            payload
        };
    }
);

// Selectors
export const getCreditsState = (state: RootState) => state.credits;
export const getCreditsData = createSelector(
    getCreditsState,
    (credits) => credits.data
);
export const getCreditData = createSelector(
    getCreditsState,
    (credits) => credits.activeData
);
export const getCreditSelectedId = createSelector(
    getCreditData,
    (activeData) => activeData.selectedId
);
export const getCompanyIdByCreditSelectedId = createSelector(
    [getCreditsData, getCreditSelectedId],
    (creditsData, selectedId) => {
        const selectedCredit = creditsData.find((credit) => credit.Id === selectedId) as any;
        return selectedCredit ? selectedCredit.CompanyId : null;
    }
);
// Reducer
export default reducer;
